import React, {FC, useEffect, useRef, useState} from "react"
import {ItemAction} from "@dv5/domain"
import {PopupMenuItem} from "./MenuItem"

export interface MenuPosition {
    x: number
    y: number
}

interface Props {
    showMenu: boolean
    menuPosition: MenuPosition
    menuItems: Array<PopupMenuItem>
    onMenuItem: (action: ItemAction, data?: string) => void
    onHide?: () => void
}

export const ItemPopupMenu: FC<Props> = ({
                                             showMenu,
                                             menuItems,
                                             menuPosition,
                                             onMenuItem,
                                             onHide,
                                         }) => {
    const menuRef = useRef<HTMLDivElement>(null)
    const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null)

    useEffect(() => {
        if (!showMenu) {
            return
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                onHide?.()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showMenu, onHide])

    useEffect(() => {
        if (!showMenu) {
            setOpenSubmenuId(null)
        }
    }, [showMenu])

    if (!showMenu) {
        return null
    }

    const handleMenuAction = (event: React.MouseEvent<HTMLDivElement>, menuItem: PopupMenuItem) => {
        event.preventDefault()
        event.stopPropagation()
        onMenuItem(menuItem.action as ItemAction, menuItem.data)
    }

    const renderMenuItem = (menuItem: PopupMenuItem, index: number, level: number) => {
        const itemId = `menu_${level}_${index}`
        if (menuItem.separator) {
            return <div key={itemId} className="popup-menu-separator" />
        }
        const hasSubmenu = menuItem.subItems && menuItem.subItems.length > 0
        const isSubmenuOpen = openSubmenuId === itemId

        const handleMouseEnter = () => {
            if (hasSubmenu) {
                setOpenSubmenuId(itemId)
            }
        }

        const handleMouseLeave = () => {
            if (hasSubmenu) {
                setOpenSubmenuId(null)
            }
        }

        return (
            <div 
                key={itemId} 
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div
                    className={`popup-menu-item ${hasSubmenu ? 'has-submenu' : ''}`}
                    onMouseDown={event => {
                        if (menuItem.action) {
                            handleMenuAction(event, menuItem)
                        }
                    }}
                >
                    {menuItem.title}
                </div>
                {hasSubmenu && isSubmenuOpen && (
                    <div className="link-popup-menu submenu">
                        {menuItem.subItems!.map((subItem, subIndex) => 
                            renderMenuItem(subItem, subIndex, level + 1)
                        )}
                    </div>
                )}
            </div>
        )
    }

    return (
        <div
            ref={menuRef}
            className="link-popup-menu"
            style={{
                position: 'absolute',
                left: menuPosition.x,
                top: menuPosition.y,
                zIndex: 1000
            }}
        >
            {menuItems.map((menuItem, index) => renderMenuItem(menuItem, index, 0))}
        </div>
    )
}
